/**
 * Super Admin customer context.
 *
 * When a Super Administrator drills into a record from Company Intelligence we
 * remember which customer they were looking at, so the existing application
 * page they land on can show a breadcrumb, a "SUPER ADMIN — viewing customer"
 * badge and a one-click route back.
 *
 * This is presentation-only: no permissions, routes or customer behaviour
 * change. Customers never write this key, and the banner is only rendered for
 * Super Administrators.
 */

export interface SaCustomerContext {
  companyId: string;
  companyName: string;
  plan?: string;
  healthScore?: number;
  /** Company Intelligence tab the drill-in started from. */
  tab?: string;
  tabLabel?: string;
  /** Human label of the opened record, e.g. "J-1012". */
  recordLabel?: string;
}

const KEY = "sa-customer-context";
export const SA_CONTEXT_EVENT = "sa-customer-context-change";

export function readSaCustomerContext(): SaCustomerContext | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SaCustomerContext;
    return parsed?.companyId ? parsed : null;
  } catch {
    return null;
  }
}

export function setSaCustomerContext(ctx: SaCustomerContext) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(ctx));
    window.dispatchEvent(new CustomEvent(SA_CONTEXT_EVENT));
  } catch {
    /* storage unavailable — the banner simply won't render */
  }
}

export function clearSaCustomerContext() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent(SA_CONTEXT_EVENT));
  } catch {
    /* no-op */
  }
}

/** Route back to the exact Company Intelligence tab the drill-in started from. */
export function saCompanyPath(ctx: SaCustomerContext): string {
  const base = `/sa/companies/${ctx.companyId}`;
  return ctx.tab && ctx.tab !== "overview" ? `${base}?tab=${ctx.tab}` : base;
}
